import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import './Carousel.css'

/* ── Slide data ─────────────────────────────────────────── */
// Each slide uses an inline SVG background pattern so the site
// works without external image dependencies.
const slides = [
  {
    id: 1,
    tag: 'Kubernetes',
    title: 'Orchestrate at Scale',
    subtitle: 'Container workloads across EKS, AKS & GKE — zero-downtime deployments, auto-scaling, and self-healing clusters.',
    cta: { label: 'Explore Platforms', to: '/platforms' },
    accent: '#00c8ff',
    icon: 'k8s',
  },
  {
    id: 2,
    tag: 'CI/CD Pipelines',
    title: 'Ship with Confidence',
    subtitle: 'End-to-end pipeline design — from code commit to production in minutes. GitOps, ArgoCD, GitHub Actions & Jenkins.',
    cta: { label: 'View Services', to: '/services' },
    accent: '#6c63ff',
    icon: 'cicd',
  },
  {
    id: 3,
    tag: 'AWS · Azure · GKE',
    title: 'Multi-Cloud Delivery',
    subtitle: 'Infrastructure as Code with Terraform & Pulumi. Cloud-native architectures that scale without vendor lock-in.',
    cta: { label: 'Learn More', to: '/about' },
    accent: '#00e676',
    icon: 'cloud',
  },
]

/* ── SVG Icons ─────────────────────────────────────────── */
function KubernetesIcon({ color }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Outer hexagon */}
      <polygon points="100,10 182,55 182,145 100,190 18,145 18,55" fill="none" stroke={color} strokeWidth="3" strokeOpacity="0.5"/>
      {/* Inner hexagon */}
      <polygon points="100,35 157,68 157,132 100,165 43,132 43,68" fill="none" stroke={color} strokeWidth="2" strokeOpacity="0.3"/>
      {/* Center wheel */}
      <circle cx="100" cy="100" r="22" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="2.5"/>
      <circle cx="100" cy="100" r="8" fill={color}/>
      {/* Spokes */}
      {[0,60,120,180,240,300].map((deg, i) => {
        const rad = (deg * Math.PI) / 180
        const x1 = 100 + 8 * Math.cos(rad)
        const y1 = 100 + 8 * Math.sin(rad)
        const x2 = 100 + 40 * Math.cos(rad)
        const y2 = 100 + 40 * Math.sin(rad)
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="3" strokeLinecap="round"/>
      })}
      {/* Node circles */}
      {[0,60,120,180,240,300].map((deg, i) => {
        const rad = (deg * Math.PI) / 180
        return (
          <circle key={i} cx={100 + 48 * Math.cos(rad)} cy={100 + 48 * Math.sin(rad)}
            r="10" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="2"/>
        )
      })}
      {/* Corner nodes on outer hex */}
      {[30,90,150,210,270,330].map((deg, i) => {
        const rad = (deg * Math.PI) / 180
        return (
          <circle key={i} cx={100 + 82 * Math.cos(rad)} cy={100 + 82 * Math.sin(rad)}
            r="7" fill={color} fillOpacity="0.6"/>
        )
      })}
    </svg>
  )
}

function CICDIcon({ color }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Pipeline track */}
      <rect x="20" y="88" width="160" height="24" rx="12" fill={color} fillOpacity="0.08" stroke={color} strokeWidth="2" strokeOpacity="0.4"/>
      {/* Stage nodes */}
      {[38, 80, 122, 164].map((cx, i) => (
        <g key={i}>
          <circle cx={cx} cy="100" r="18" fill={color} fillOpacity={i < 3 ? 0.2 : 0.05} stroke={color} strokeWidth="2"/>
          <circle cx={cx} cy="100" r="8" fill={i < 3 ? color : 'none'} stroke={color} strokeWidth={i < 3 ? 0 : 1.5} strokeOpacity="0.6"/>
        </g>
      ))}
      {/* Labels */}
      {['Build','Test','Push','Deploy'].map((label, i) => (
        <text key={i} x={[38,80,122,164][i]} y="130" textAnchor="middle"
          fill={color} fontSize="10" fontFamily="JetBrains Mono, monospace" fillOpacity="0.8">
          {label}
        </text>
      ))}
      {/* Arrows between nodes */}
      {[[56,62],[98,104],[140,146]].map(([x1,x2], i) => (
        <line key={i} x1={x1} y1="100" x2={x2} y2="100" stroke={color} strokeWidth="2" strokeOpacity="0.6"/>
      ))}
      {/* Top: Git branch */}
      <path d="M60,88 Q60,55 100,55 Q140,55 140,88" stroke={color} strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.4" fill="none"/>
      <circle cx="60" cy="55" r="6" fill={color} fillOpacity="0.5"/>
      <circle cx="140" cy="55" r="6" fill={color} fillOpacity="0.5"/>
      <circle cx="100" cy="48" r="5" fill={color}/>
      <text x="100" y="35" textAnchor="middle" fill={color} fontSize="9" fontFamily="JetBrains Mono, monospace" fillOpacity="0.7">main</text>
      {/* Bottom: Helm deploy */}
      <path d="M122,118 L122,148 L160,148" stroke={color} strokeWidth="1.5" strokeDasharray="3 3" strokeOpacity="0.4" fill="none"/>
      <circle cx="160" cy="148" r="10" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5"/>
      <text x="160" y="152" textAnchor="middle" fill={color} fontSize="8" fontFamily="JetBrains Mono, monospace">K8s</text>
    </svg>
  )
}

function CloudIcon({ color }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Cloud shape */}
      <path d="M150,115 C165,115 175,103 175,88 C175,73 163,61 148,61 C146,44 132,30 115,30 C97,30 82,44 82,62 C82,63 82,64 82,65 C70,68 62,79 62,92 C62,105 72,115 85,115 Z"
        fill={color} fillOpacity="0.12" stroke={color} strokeWidth="2.5"/>
      {/* AWS node */}
      <rect x="50" y="135" width="28" height="22" rx="5" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.5"/>
      <text x="64" y="150" textAnchor="middle" fill={color} fontSize="8" fontFamily="JetBrains Mono, monospace">AWS</text>
      {/* Azure node */}
      <rect x="86" y="135" width="28" height="22" rx="5" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.5"/>
      <text x="100" y="150" textAnchor="middle" fill={color} fontSize="7" fontFamily="JetBrains Mono, monospace">Azure</text>
      {/* GKE node */}
      <rect x="122" y="135" width="28" height="22" rx="5" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.5"/>
      <text x="136" y="150" textAnchor="middle" fill={color} fontSize="8" fontFamily="JetBrains Mono, monospace">GKE</text>
      {/* Connection lines from cloud */}
      <line x1="64" y1="115" x2="64" y2="135" stroke={color} strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.6"/>
      <line x1="100" y1="115" x2="100" y2="135" stroke={color} strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.6"/>
      <line x1="136" y1="115" x2="136" y2="135" stroke={color} strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.6"/>
      {/* Terraform symbol */}
      <path d="M95,70 L105,76 L105,88 L95,82 Z" fill={color} fillOpacity="0.8"/>
      <path d="M106,64 L116,70 L116,82 L106,76 Z" fill={color} fillOpacity="0.5"/>
      <path d="M95,84 L105,90 L105,102 L95,96 Z" fill={color} fillOpacity="0.35"/>
    </svg>
  )
}

const ICONS = { k8s: KubernetesIcon, cicd: CICDIcon, cloud: CloudIcon }

/* ── Carousel ─────────────────────────────────────────────  */
export default function Carousel() {
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)

  const next = useCallback(() => setCurrent(c => (c + 1) % slides.length), [])
  const prev = useCallback(() => setCurrent(c => (c - 1 + slides.length) % slides.length), [])

  useEffect(() => {
    if (paused) return
    const id = setInterval(next, 5000)
    return () => clearInterval(id)
  }, [paused, next])

  const slide = slides[current]
  const Icon = ICONS[slide.icon]

  return (
    <div
      className="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Grid background */}
      <div className="carousel__grid" />

      {/* Glow orb */}
      <div className="carousel__glow" style={{ background: `radial-gradient(ellipse at center, ${slide.accent}22 0%, transparent 70%)` }} />

      <div className="carousel__slide container">
        {/* Left — Text */}
        <div className="carousel__text" key={slide.id}>
          <span className="badge" style={{ color: slide.accent, borderColor: `${slide.accent}44`, background: `${slide.accent}12` }}>
            {slide.tag}
          </span>
          <h1 className="carousel__title">{slide.title}</h1>
          <p className="carousel__sub">{slide.subtitle}</p>
          <div className="carousel__actions">
            <Link to={slide.cta.to} className="btn btn--primary" style={{ background: slide.accent }}>
              {slide.cta.label}
            </Link>
            <Link to="/services" className="btn btn--outline" style={{ color: slide.accent, borderColor: slide.accent }}>
              Our Services
            </Link>
          </div>
        </div>

        {/* Right — Icon */}
        <div className="carousel__visual" key={`v-${slide.id}`}>
          <div className="carousel__icon-wrap" style={{ borderColor: `${slide.accent}30` }}>
            <Icon color={slide.accent} />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="carousel__controls">
        <button className="carousel__arrow" onClick={prev} aria-label="Previous">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="carousel__dots">
          {slides.map((s, i) => (
            <button
              key={s.id}
              className={`carousel__dot ${i === current ? 'carousel__dot--active' : ''}`}
              style={i === current ? { background: slide.accent } : {}}
              onClick={() => setCurrent(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        <button className="carousel__arrow" onClick={next} aria-label="Next">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 4L14 10L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div className="carousel__progress">
        <div
          className="carousel__progress-bar"
          style={{
            background: slide.accent,
            animationPlayState: paused ? 'paused' : 'running',
            animationDuration: '5s',
          }}
          key={`${current}-${paused}`}
        />
      </div>
    </div>
  )
}
